import React, { useMemo } from 'react'
import { InView } from 'react-intersection-observer'
import { useHistory } from 'react-router'

import {
  FolderTerm,
  routes,
  useFolderTerm,
  useUserProfile,
} from '@sketch/modules-common'

import {
  Breakpoint,
  Table,
  ModalRestrictor,
  useModalContext,
  useResponsiveDropdown,
  LoadingState,
  pluralize,
} from '@sketch/components'

import { ShareActionsDropdownTrashed } from '../../../shares/components/ShareActionsDropdownTrashed'

import {
  StorageItemFragment,
  useGetDocumentListSettingsQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

import { ResponsiveValues } from '@sketch/global-styles'

import {
  DocumentGridItemPlaceholder,
  DocumentTableItemPlaceholder,
  ConnectedDocumentItem,
} from '../../../shares/components/DocumentItem'

import { ProjectItem } from '../../../shares/components/ProjectItem'

import { ShareTrashedModal } from '../../../shares/components/ShareTrashedModal'
import { thumbnailForPreviewFile } from '../../../shares/utils'

import {
  ActionTableCell,
  Footer,
  TableWrapper,
  FocusLink,
  HeaderTableCell,
} from '../../../shares/components/DocumentItemsLayout/DocumentItemsLayout.styles'

import {
  DropdownButton,
  DropdownButtonEllipsis,
  DropdownWrapper,
} from '../../../shares/components/DocumentItem/DocumentItem.styles'
import { ProjectTrashedDropdown } from '../../../projects/components/ProjectDropdown'
import { useCurrentWorkspaceInPrivateWorkspaceRoute } from '../../../workspace/hooks'
import { useSelectionContext } from '../SelectionContext'
import { AnimatedSelectionBottomSink } from '../SelectionBottomSink'

import { AspectRatioGridWrapper } from './TrashItemsLayout.styles'

// ShareListItemFragment should be removed once BE returns the newer types in the getTrash
// https://github.com/sketch-hq/Cloud/issues/11226
type Item = StorageItemFragment
type RenderItem = Item | 'listener' | 'placeholder'

// eslint-disable-next-line @typescript-eslint/no-restricted-types
type Project = Extract<StorageItemFragment, { __typename: 'Project' }>
// eslint-disable-next-line @typescript-eslint/no-restricted-types
type Share = Extract<StorageItemFragment, { __typename: 'Share' }>

interface TrashItemsLayoutProps {
  items: Item[]
  loading: boolean
  onLoadMore: () => Promise<any>
  totalItemsCount: number
  header?: React.ReactNode
  workspaceId: string
  userRole: WorkspaceMinimalFragment['userRole']
}

export const TABLE_HEADER = (folderTerm: FolderTerm) => [
  { label: 'Name', customCell: HeaderTableCell },
  { label: folderTerm.upper, customCell: HeaderTableCell },
  { label: 'Deleted', customCell: HeaderTableCell },
  { label: 'Updated By', customCell: HeaderTableCell },
  { label: '', customCell: ActionTableCell },
]

const COLUMNS: ResponsiveValues<number> = [2, 2, 3, 3, 3, 4, 5, 6]
const GUTTER: ResponsiveValues<number> = [16, 16, 16, 16, 40, 40, 40]
const VERTICAL_SPACE: ResponsiveValues<number> = [24, 24, 24, 24, 40, 40, 40]
const PAGE_SIZE = 20

const buildPlaceholderArray = (number: number) => {
  if (number <= 0) {
    return []
  }

  if (number > PAGE_SIZE) {
    return ['listener', ...Array(PAGE_SIZE - 1).fill('placeholder')] as const
  }

  return ['listener', ...Array(number - 1).fill('placeholder')] as const
}

const ActionPopoverWrapper: React.FCC = ({ children }) => <div>{children}</div>

const ResponsiveDropdown: React.FCC = ({ children }) => {
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ActionPopoverWrapper,
    dropdownProps: {
      children,
    },
    placement: 'bottom-end',
  })

  return (
    <DropdownWrapper onClick={event => event.stopPropagation()}>
      <DropdownButton {...buttonProps}>
        <DropdownButtonEllipsis aria-label="Document Settings" />
        <span className="sr-only">Document Settings</span>
      </DropdownButton>
      {content}
    </DropdownWrapper>
  )
}

const renderDropdown = (children: React.ReactElement) => (
  <ResponsiveDropdown>{children}</ResponsiveDropdown>
)

// Auxiliary components
const GridLayout = ({ children }: React.PropsWithChildren) => (
  <AspectRatioGridWrapper
    columns={COLUMNS}
    verticalRowSpace={VERTICAL_SPACE}
    gutterSize={GUTTER}
  >
    {children}
  </AspectRatioGridWrapper>
)

const TrashItemsLayout: React.FCC<TrashItemsLayoutProps> = props => {
  const { items, onLoadMore, loading, totalItemsCount, header, workspaceId } =
    props

  const { showModal } = useModalContext()
  const { userCanAdminister } = useCurrentWorkspaceInPrivateWorkspaceRoute()
  const history = useHistory()

  // Load the document layout
  const { data } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  // Get the User Identifier to show the "You" label
  const { data: userData } = useUserProfile()
  const userIdentifier = userData?.me.identifier

  const folderTerm = useFolderTerm()
  const tableHeader = useMemo(() => TABLE_HEADER(folderTerm), [folderTerm])

  // Selection
  const { toggleItemSelection, showSelectionCheckbox, isSelected } =
    useSelectionContext()

  if (loading && items.length === 0) {
    return <LoadingState />
  }

  const placeholders = buildPlaceholderArray(
    (totalItemsCount || 0) - items.length
  )

  // Merge the items with the placeholders have them rendered together
  // This prevents the page from bumping when the placeholders are replaced
  // with actual items
  const sharesAndPlaceholders = [...items, ...placeholders]

  const onShareAction = (item: Share) => {
    showModal(ShareTrashedModal, {
      share: item,
    })
  }

  const renderProjectDropdownMenu = (item: Project) => (
    <ProjectTrashedDropdown
      project={item}
      workspace={{ identifier: workspaceId, userCanAdminister }}
    />
  )

  const renderGridItem = (item: RenderItem, index: number) => {
    if (item === 'listener' || item === 'placeholder') {
      return (
        <DocumentGridItemPlaceholder
          key={index}
          inViewListener={
            item === 'listener' && (
              <InView onChange={inView => inView && onLoadMore?.()}>
                <span className="sr-only">Loading Placeholder</span>
              </InView>
            )
          }
        />
      )
    }

    if (item.__typename === 'Project') {
      const thumbnails = item.previews.map(item => {
        const imageSource = thumbnailForPreviewFile(item.file)
        return { name: item.name, imageSource }
      })
      const childrenItemCount = item.sharesCount + item.nestedProjectsCount

      return (
        <ModalRestrictor key={item.identifier}>
          <FocusLink
            to={routes.WORKSPACE_TRASH_PROJECT.create({
              projectId: item.identifier,
              workspaceId,
            })}
            key={item.identifier}
          >
            <ProjectItem.Card
              identifier={item.identifier}
              name={item.name}
              deletedAt={item.deletedAt!}
              childrenItemCount={childrenItemCount}
              childrenItemLabel={pluralize('Item', 'Items', childrenItemCount)}
              thumbnails={thumbnails}
              renderDropdownItems={() => renderProjectDropdownMenu(item)}
              showSelectionCheckbox={showSelectionCheckbox}
              selected={isSelected(item)}
              onSelectChange={() => {
                toggleItemSelection(item)
              }}
            />
          </FocusLink>
        </ModalRestrictor>
      )
    }

    return (
      <ConnectedDocumentItem
        key={item.identifier}
        workspaceId={workspaceId}
        share={item}
        renderDropdown={() =>
          renderDropdown(<ShareActionsDropdownTrashed share={item} />)
        }
        presentation="grid"
        showProjectName
        onClick={() => onShareAction(item)}
        userIdentifier={userIdentifier}
        showSelectionCheckbox={showSelectionCheckbox}
        selected={isSelected(item)}
        onSelectChange={() => {
          toggleItemSelection(item)
        }}
      />
    )
  }

  const renderTableItem = (item: RenderItem) => {
    if (item === 'listener' || item === 'placeholder') {
      return (
        <DocumentTableItemPlaceholder
          columnCount={tableHeader.length}
          inViewListener={
            item === 'listener' && (
              <InView onChange={inView => inView && onLoadMore?.()}>
                <span className="sr-only">Loading Placeholder</span>
              </InView>
            )
          }
        />
      )
    }

    if (item.__typename === 'Project') {
      return (
        <ModalRestrictor key={item.identifier}>
          <ProjectItem.ListItem
            name={item.name}
            deletedAt={item.deletedAt!}
            onClick={event => {
              const link = routes.WORKSPACE_TRASH_PROJECT.create({
                projectId: item.identifier,
                workspaceId,
              })

              if (event.metaKey) {
                /* Simulate the link opening with meta key */
                window.open(link, '_blank')
              } else {
                history.push(link)
              }
            }}
            renderDropdownItems={() => renderProjectDropdownMenu(item)}
            identifier={item.identifier}
            selected={isSelected(item)}
            onSelectChange={() => {
              toggleItemSelection(item)
            }}
          />
        </ModalRestrictor>
      )
    }

    return (
      <ConnectedDocumentItem
        key={item.identifier}
        workspaceId={workspaceId}
        share={item}
        renderDropdown={() =>
          renderDropdown(<ShareActionsDropdownTrashed share={item} />)
        }
        presentation="list"
        showProjectName
        onClick={() => onShareAction(item)}
        userIdentifier={userIdentifier}
        selected={isSelected(item)}
        onSelectChange={() => {
          toggleItemSelection(item)
        }}
      />
    )
  }

  const renderLayout = (isMobile: boolean) => {
    if (documentsLayout === 'LIST') {
      return (
        <TableWrapper>
          <Table
            header={tableHeader}
            items={sharesAndPlaceholders}
            renderItemKey={item =>
              // if it's not a share the table will generate a index based key
              typeof item === 'object' ? item.identifier : undefined
            }
            renderItem={renderTableItem}
            evenColumns={!isMobile}
          />
        </TableWrapper>
      )
    }

    return <GridLayout>{sharesAndPlaceholders.map(renderGridItem)}</GridLayout>
  }

  return (
    <>
      {header}
      <Breakpoint on="sm">{matches => renderLayout(!matches)}</Breakpoint>
      <AnimatedSelectionBottomSink workspaceIdentifier={workspaceId} />

      <Footer />
    </>
  )
}

export default TrashItemsLayout
